/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { action, computed, makeObservable, observable } from "mobx"

/**
 * The download page's current screen. There are no per-screen URLs: the whole
 * flow lives under the single `/heart-report` route (the emailed link) and a
 * refresh restarts safely at the landing screen.
 *
 *   requestCode  — landing: explains the code and offers to send it (nothing is sent on load)
 *   verify       — the six-box code gate
 *   download     — the report's header facts and the download button
 *   unavailable  — the link can't be used (expired, or the report was withdrawn)
 */
type HeartReportView = "requestCode" | "verify" | "download" | "unavailable"

class HeartReportUiStore {
  private _view: HeartReportView

  constructor(initialView: HeartReportView = "requestCode") {
    // Assign before makeObservable so `_view` is an own property under both the
    // CRA build (useDefineForClassFields) and ts-jest, or MobX can't find it.
    this._view = initialView
    makeObservable<HeartReportUiStore, "_view">(this, {
      _view: observable,
      view: computed,
      goTo: action,
    })
  }

  get view(): HeartReportView {
    return this._view
  }

  goTo(view: HeartReportView): void {
    this._view = view
  }
}

export default HeartReportUiStore
export type { HeartReportView }
